import { RouteProp, useRoute } from "@react-navigation/native";
import { useResponsiveProp } from "@shopify/restyle";
import graphql from "babel-plugin-relay/macro";
import { Provider } from "jotai";
import { Suspense, useCallback, useEffect, useState } from "react";
import { useSharedValue } from "react-native-reanimated";
import { PreloadedQuery, usePreloadedQuery, useQueryLoader } from "react-relay";

import { AgendaLayout } from "@whoosh/design-system/src/apps/PatronApp/layout/AgendaLayout";
import { Loader } from "@whoosh/design-system/src/components/display";
import { Box } from "@whoosh/design-system/src/components/primitive";

import { LessonsStackNavigatorParamList } from "../../../../navigators/LessonsStackNavigator/types";
import { LessonsSignedOutBanner } from "../../components/LessonsSignedOutBanner";
import { LessonsScreensQuery } from "./__generated__/LessonsScreensQuery.graphql";
import { InstructorList } from "./components/InstructorList";
import { LessonsHeader } from "./components/LessonsHeader";
import { LessonsSidebar } from "./components/LessonsSidebar";

export const LessonsScreen = () => {
  const route =
    useRoute<RouteProp<LessonsStackNavigatorParamList, "LessonsHome">>();

  const facilitySlug = route?.params?.facilitySlug;

  const [queryRef, loadQuery] = useQueryLoader<LessonsScreensQuery>(
    LessonsScreenQueryString
  );

  const fetchLessonsQuery = useCallback(() => {
    if (loadQuery && facilitySlug) {
      loadQuery({ facilitySlug }, { fetchPolicy: "store-and-network" });
    }
  }, [facilitySlug, loadQuery]);

  useEffect(() => {
    fetchLessonsQuery();
  }, [facilitySlug, fetchLessonsQuery, loadQuery]);

  return (
    <Box flex={1} safeAreaTop bg="surface-primary">
      <Suspense fallback={<Loader />}>
        {queryRef ? (
          <LessonsScreenInner
            queryRef={queryRef}
            refetchCallback={fetchLessonsQuery}
          />
        ) : null}
      </Suspense>
    </Box>
  );
};

type InnerProps = {
  queryRef: PreloadedQuery<LessonsScreensQuery>;
  refetchCallback: () => void;
};

const LessonsScreenInner = ({ queryRef, refetchCallback }: InnerProps) => {
  const [isAuthModalOpen, setIsAuthModalOpen] = useState(false);
  const isMobile = useResponsiveProp({ base: 1, lg: 0 });
  const data = usePreloadedQuery(LessonsScreenQueryString, queryRef);
  const [scrolled, setScrolled] = useState(false);
  const scrollY = useSharedValue(0);

  const completeCallback = useCallback(() => {
    refetchCallback();
    setIsAuthModalOpen(false);
  }, [refetchCallback]);

  return (
    <>
      <WhiteBoxToCoverHeaderShadow />
      <Provider>
        <AgendaLayout
          bgColor="surface-primary"
          shouldCenter
          offsetTop={isMobile ? true : false}
          header={
            <LessonsHeader
              facilityRef={data.facility ?? null}
              sessionRef={data.session ?? null}
              scrolled={scrolled}
              scrollY={scrollY}
            />
          }
          agenda={
            <>
              <InstructorList
                facilityRef={data.facility ?? null}
                sessionRef={data.session ?? null}
                setScrolled={setScrolled}
                scrollY={scrollY}
              />
              <LessonsSignedOutBanner
                sessionRef={data.session ?? null}
                completeCallback={completeCallback}
                cancelCallback={() => setIsAuthModalOpen(false)}
                authModalState={[isAuthModalOpen, setIsAuthModalOpen]}
              />
            </>
          }
          sidebarContent={
            <LessonsSidebar
              facilityRef={data.facility ?? null}
              sessionRef={data.session ?? null}
            />
          }
        />
      </Provider>
    </>
  );
};

const LessonsScreenQueryString = graphql`
  query LessonsScreensQuery($facilitySlug: String!) {
    facility(slug: $facilitySlug) {
      id
      ...LessonsHeaderFragment_facility
      ...InstructorListFragment_facility
      ...LessonsSidebar_facility
    }
    session {
      ...LessonsHeaderFragment_session
      ...InstructorListFragment_session
      ...LessonsSignedOutBanner_session
      ...LessonsSidebar_session
    }
  }
`;

const WhiteBoxToCoverHeaderShadow = () => {
  return (
    <Box
      height={60}
      style={{ zIndex: 1, marginTop: -60 }}
      bg="surface-primary"
    />
  );
};
